import { useMeasureDirty } from '@app/core/hooks/useMeasureDirty';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { webApi } from '@mi-front/api-clients';
import { getBrowserTimezone } from '@mi-front/core';
import { Alert, Box, Paper, Stack } from '@mui/material';
import { useMemo, useRef } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { IssuesTable } from '../issues-table/IssuesTable';
import { useStationFilterModel } from '../store/useViewModel';
import { DisabledHatchPatternDef } from './DisabledHatchPatternDef';
import { groupStationsByUtcOffsetPairForReport } from './group-stations-by-timezone';
import { KpiCards } from './KpiCards';
import { TimelineFilterHeader } from './TimelineFilterHeader';
import { TimelineTooltipLayer } from './TimelineTooltipLayer';
import { TimezoneGroupSection } from './TimezoneGroupSection';

const noMatchingStationsMessage = defineMessage({
  id: 'logger.reports.streamQuality.stationStatusFilter.noMatchingStations',
  defaultMessage: 'Ninguna emisora coincide con los filtros seleccionados. Elegí otra combinación.',
});

export type ReportResultProps = {
  report: webApi.StreamQualityReportResponseDto;
};

/**
 * Stream quality report result: KPIs, sticky range brush, timelines grouped by UTC offset pair
 * (report start/end), and one aggregated issues table below.
 */
export function ReportResult({ report }: ReportResultProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineHeaderRef = useRef<HTMLDivElement>(null);
  const { width } = useMeasureDirty(containerRef);
  const { height: timelineHeaderHeight } = useMeasureDirty(timelineHeaderRef);

  const { filteredStationIds, stationFilterExcludesAll } = useStationFilterModel(report);
  const finder = useStationsFinder();

  const offsetGroups = useMemo(
    () =>
      groupStationsByUtcOffsetPairForReport(
        report,
        filteredStationIds,
        (stationId) => finder(stationId)?.timezone,
        getBrowserTimezone(),
      ),
    [report, filteredStationIds, finder],
  );

  const noMatchingStationsAlert = stationFilterExcludesAll ? (
    <Alert severity="info" sx={{ mt: 2 }}>
      <FormattedMessage {...noMatchingStationsMessage} />
    </Alert>
  ) : null;

  return (
    <Box sx={{ position: 'relative' }}>
      <DisabledHatchPatternDef />
      <TimelineTooltipLayer />
      <KpiCards report={report} />
      <Paper sx={{ p: 2 }}>
        <Box ref={containerRef} sx={{ width: '100%' }}>
          <Box>
            {!width ? null : (
              <>
                <TimelineFilterHeader ref={timelineHeaderRef} width={width} report={report} />
                {noMatchingStationsAlert}
                {!stationFilterExcludesAll ? (
                  <>
                    <Stack spacing={2} sx={{ mt: 0 }}>
                      {offsetGroups.map((group) => (
                        <TimezoneGroupSection
                          key={group.id}
                          report={report}
                          width={width}
                          groupLabel={group.label}
                          stationIds={group.stationIds}
                          groupTimeZone={group.timeZones[0] ?? getBrowserTimezone()}
                          stickyAxisTop={timelineHeaderHeight}
                          hideGroupTitleRow={offsetGroups.length === 1}
                        />
                      ))}
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <IssuesTable report={report} />
                    </Box>
                  </>
                ) : null}
              </>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
